/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getOptionDecoder,
  getOptionEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type Option,
  type OptionOrNullable,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  expectSome,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const ISSUE_CREDENTIAL_DISCRIMINATOR = new Uint8Array([
  255, 193, 171, 224, 68, 171, 194, 87,
]);

export function getIssueCredentialDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    ISSUE_CREDENTIAL_DISCRIMINATOR,
  );
}

export type IssueCredentialInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountCredential extends string | AccountMeta<string> = string,
  TAccountCredentialTemplate extends string | AccountMeta<string> = string,
  TAccountCredentialType extends string | AccountMeta<string> = string,
  TAccountSubject extends string | AccountMeta<string> = string,
  TAccountIssuer extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    "11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountCredential extends string
        ? WritableAccount<TAccountCredential>
        : TAccountCredential,
      TAccountCredentialTemplate extends string
        ? WritableAccount<TAccountCredentialTemplate>
        : TAccountCredentialTemplate,
      TAccountCredentialType extends string
        ? WritableAccount<TAccountCredentialType>
        : TAccountCredentialType,
      TAccountSubject extends string
        ? ReadonlyAccount<TAccountSubject>
        : TAccountSubject,
      TAccountIssuer extends string
        ? WritableSignerAccount<TAccountIssuer> &
            AccountSignerMeta<TAccountIssuer>
        : TAccountIssuer,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type IssueCredentialInstructionData = {
  discriminator: ReadonlyUint8Array;
  credentialId: string;
  subjectDataHash: ReadonlyUint8Array;
  subjectDataUri: string;
  expiresAt: Option<bigint>;
  sourceAccount: Option<Address>;
};

export type IssueCredentialInstructionDataArgs = {
  credentialId: string;
  subjectDataHash: ReadonlyUint8Array;
  subjectDataUri: string;
  expiresAt: OptionOrNullable<number | bigint>;
  sourceAccount: OptionOrNullable<Address>;
};

export function getIssueCredentialInstructionDataEncoder(): Encoder<IssueCredentialInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["credentialId", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["subjectDataHash", fixEncoderSize(getBytesEncoder(), 32)],
      [
        "subjectDataUri",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
      ["expiresAt", getOptionEncoder(getI64Encoder())],
      ["sourceAccount", getOptionEncoder(getAddressEncoder())],
    ]),
    (value) => ({ ...value, discriminator: ISSUE_CREDENTIAL_DISCRIMINATOR }),
  );
}

export function getIssueCredentialInstructionDataDecoder(): Decoder<IssueCredentialInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["credentialId", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["subjectDataHash", fixDecoderSize(getBytesDecoder(), 32)],
    ["subjectDataUri", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["expiresAt", getOptionDecoder(getI64Decoder())],
    ["sourceAccount", getOptionDecoder(getAddressDecoder())],
  ]);
}

export function getIssueCredentialInstructionDataCodec(): Codec<
  IssueCredentialInstructionDataArgs,
  IssueCredentialInstructionData
> {
  return combineCodec(
    getIssueCredentialInstructionDataEncoder(),
    getIssueCredentialInstructionDataDecoder(),
  );
}

export type IssueCredentialAsyncInput<
  TAccountCredential extends string = string,
  TAccountCredentialTemplate extends string = string,
  TAccountCredentialType extends string = string,
  TAccountSubject extends string = string,
  TAccountIssuer extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  credential?: Address<TAccountCredential>;
  credentialTemplate: Address<TAccountCredentialTemplate>;
  credentialType: Address<TAccountCredentialType>;
  subject: Address<TAccountSubject>;
  issuer: TransactionSigner<TAccountIssuer>;
  systemProgram?: Address<TAccountSystemProgram>;
  credentialId: IssueCredentialInstructionDataArgs["credentialId"];
  subjectDataHash: IssueCredentialInstructionDataArgs["subjectDataHash"];
  subjectDataUri: IssueCredentialInstructionDataArgs["subjectDataUri"];
  expiresAt: IssueCredentialInstructionDataArgs["expiresAt"];
  sourceAccount: IssueCredentialInstructionDataArgs["sourceAccount"];
};

export async function getIssueCredentialInstructionAsync<
  TAccountCredential extends string,
  TAccountCredentialTemplate extends string,
  TAccountCredentialType extends string,
  TAccountSubject extends string,
  TAccountIssuer extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: IssueCredentialAsyncInput<
    TAccountCredential,
    TAccountCredentialTemplate,
    TAccountCredentialType,
    TAccountSubject,
    TAccountIssuer,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  IssueCredentialInstruction<
    TProgramAddress,
    TAccountCredential,
    TAccountCredentialTemplate,
    TAccountCredentialType,
    TAccountSubject,
    TAccountIssuer,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    credential: { value: input.credential ?? null, isWritable: true },
    credentialTemplate: {
      value: input.credentialTemplate ?? null,
      isWritable: true,
    },
    credentialType: { value: input.credentialType ?? null, isWritable: true },
    subject: { value: input.subject ?? null, isWritable: false },
    issuer: { value: input.issuer ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.credential.value) {
    accounts.credential.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([99, 114, 101, 100, 101, 110, 116, 105, 97, 108]),
        ),
        getAddressEncoder().encode(
          expectAddress(accounts.credentialTemplate.value),
        ),
        getAddressEncoder().encode(expectAddress(accounts.subject.value)),
        getUtf8Encoder().encode(expectSome(args.credentialId)),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.credential),
      getAccountMeta(accounts.credentialTemplate),
      getAccountMeta(accounts.credentialType),
      getAccountMeta(accounts.subject),
      getAccountMeta(accounts.issuer),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getIssueCredentialInstructionDataEncoder().encode(
      args as IssueCredentialInstructionDataArgs,
    ),
    programAddress,
  } as IssueCredentialInstruction<
    TProgramAddress,
    TAccountCredential,
    TAccountCredentialTemplate,
    TAccountCredentialType,
    TAccountSubject,
    TAccountIssuer,
    TAccountSystemProgram
  >);
}

export type IssueCredentialInput<
  TAccountCredential extends string = string,
  TAccountCredentialTemplate extends string = string,
  TAccountCredentialType extends string = string,
  TAccountSubject extends string = string,
  TAccountIssuer extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  credential: Address<TAccountCredential>;
  credentialTemplate: Address<TAccountCredentialTemplate>;
  credentialType: Address<TAccountCredentialType>;
  subject: Address<TAccountSubject>;
  issuer: TransactionSigner<TAccountIssuer>;
  systemProgram?: Address<TAccountSystemProgram>;
  credentialId: IssueCredentialInstructionDataArgs["credentialId"];
  subjectDataHash: IssueCredentialInstructionDataArgs["subjectDataHash"];
  subjectDataUri: IssueCredentialInstructionDataArgs["subjectDataUri"];
  expiresAt: IssueCredentialInstructionDataArgs["expiresAt"];
  sourceAccount: IssueCredentialInstructionDataArgs["sourceAccount"];
};

export function getIssueCredentialInstruction<
  TAccountCredential extends string,
  TAccountCredentialTemplate extends string,
  TAccountCredentialType extends string,
  TAccountSubject extends string,
  TAccountIssuer extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: IssueCredentialInput<
    TAccountCredential,
    TAccountCredentialTemplate,
    TAccountCredentialType,
    TAccountSubject,
    TAccountIssuer,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): IssueCredentialInstruction<
  TProgramAddress,
  TAccountCredential,
  TAccountCredentialTemplate,
  TAccountCredentialType,
  TAccountSubject,
  TAccountIssuer,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    credential: { value: input.credential ?? null, isWritable: true },
    credentialTemplate: {
      value: input.credentialTemplate ?? null,
      isWritable: true,
    },
    credentialType: { value: input.credentialType ?? null, isWritable: true },
    subject: { value: input.subject ?? null, isWritable: false },
    issuer: { value: input.issuer ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.credential),
      getAccountMeta(accounts.credentialTemplate),
      getAccountMeta(accounts.credentialType),
      getAccountMeta(accounts.subject),
      getAccountMeta(accounts.issuer),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getIssueCredentialInstructionDataEncoder().encode(
      args as IssueCredentialInstructionDataArgs,
    ),
    programAddress,
  } as IssueCredentialInstruction<
    TProgramAddress,
    TAccountCredential,
    TAccountCredentialTemplate,
    TAccountCredentialType,
    TAccountSubject,
    TAccountIssuer,
    TAccountSystemProgram
  >);
}

export type ParsedIssueCredentialInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    credential: TAccountMetas[0];
    credentialTemplate: TAccountMetas[1];
    credentialType: TAccountMetas[2];
    subject: TAccountMetas[3];
    issuer: TAccountMetas[4];
    systemProgram: TAccountMetas[5];
  };
  data: IssueCredentialInstructionData;
};

export function parseIssueCredentialInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedIssueCredentialInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 6) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      credential: getNextAccount(),
      credentialTemplate: getNextAccount(),
      credentialType: getNextAccount(),
      subject: getNextAccount(),
      issuer: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getIssueCredentialInstructionDataDecoder().decode(instruction.data),
  };
}
